import { UsageRatingRulesService } from 'src/app/angularJS/services/usage-rating-rules/usage-rating-rules.service';
import { UserService } from 'src/app/angularJS/services/user/user.service';
import { LinkBuilder } from 'src/app/angularJS/services/link-builder.service';
import { QueryBuilder } from 'src/app/angularJS/services/query-builder.service';
import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';
import { UserMessagesService } from 'src/app/angularJS/services/user-messages.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';
import { DialogService } from 'src/app/angularJS/services/dialog/dialog.service';

export class UsageRatingRulesController {
  public static $inject = [
    '$rootScope',
    'usageRatingRulesService',
    'userService',
    'LinkBuilder',
    'QueryBuilder',
    'ACTToolbarService',
    'UserMessages',
    'PrimaryAction',
    '$state',
    'breadcrumbService',
    'dialogService',
    '$translate'
  ];
  public usageRatingRules: Array<InterACT.Interfaces.UsageRatingRules.UsageRatingRule> = [];
  public paginatorData: InterACT.Interfaces.IPaginationData = {
    currentPage: 1,
    perPageCount: 15,
    totalCount: 0
  };
  public ruleFilters = {
    validFrom: null,
    validUntil: null
  };

  constructor(
    private $rootScope: InterACT.Interfaces.IActoraRootScope,
    private usageRatingRulesService: UsageRatingRulesService,
    private userService: UserService,
    private linkBuilder: LinkBuilder,
    private queryBuilder: QueryBuilder,
    private toolbarService: ACTToolbarService,
    private userMessagesService: UserMessagesService,
    private PrimaryActionService,
    private $state: ng.ui.IStateService,
    private breadcrumbService: BreadcrumbService,
    private dialogService: DialogService,
    public translate: ng.translate.ITranslateService) {
    this.activate();
  }

  /**
   * A method is called whenever any of the filters on this controller have changed
   */
  public onFilterChanged = () => {
    return this.getUsageRatingRules(this.paginatorData.currentPage, this.paginatorData.perPageCount);
  };

  /**
   * A method that gets a page worth of usage rating rules
   * @param page the page number to retrieve
   * @param perPage the number of usage rating rules to retrieve per page
   */
  public getUsageRatingRules = (page: number, perPage: number) => {
    const getUsageRatingRules = () => {
      this.$rootScope.showSpinner = true;
      const query = this.constructQueryString();

      this.usageRatingRulesService.getList(page, perPage, query).then((response) => {
        this.paginatorData.totalCount = response.meta.totalCount;
        this.$rootScope.showSpinner = false;
        this.usageRatingRules = response.usageRatingRules.map((usageRatingRule) => {
          return usageRatingRule.usageRatingRule;
        });
      });
    };

    if (!this.userService.selectedScheme) {
      const callbackRef = this.userService.registerSchemeCallback(() => {
        this.userService.unregisterCallback(callbackRef);
        getUsageRatingRules();
      });

      return;
    }

    getUsageRatingRules();
  };

  /**
   * A method that shows the usage rating dialog passing in the usage rating we wish to edit
   *
   * @param usageRatingRule The usage rating rule to be edited
   */
  public editUsageRatingRule = (usageRatingRule: InterACT.Interfaces.UsageRatingRules.UsageRatingRule) => {
    const copyOfUsageRatingRule = angular.copy(usageRatingRule);

    this.showUsageRatingDialog(copyOfUsageRatingRule);
  };

  /**
   * A method that prompts the user to delete the supplied usage-rating-rule
   * @param usageRatingRule The usage rating rule to be deleted
   */
  public deleteUsageRatingRule = (usageRatingRule: InterACT.Interfaces.UsageRatingRules.UsageRatingRule) => {
    this.userMessagesService.buildConfirmDialog(
      'DELETE_USAGE_RATING_RULE',
      'CONFIRM_DIALOG.USAGE_RATING_RULE.DELETE_CONFIRM',
      undefined,
      'CONFIRM_DIALOG.USAGE_RATING_RULE.CONFIRM',
      'CONFIRM_DIALOG.USAGE_RATING_RULE.CANCEL'
    ).then(() => {
      this.usageRatingRulesService.delete(usageRatingRule).then(() => {
        this.userMessagesService.showToast('USAGE_RATING_RULE_DELETED');
        this.getUsageRatingRules(this.paginatorData.currentPage, this.paginatorData.perPageCount);
      });
    });
  };

  /**
   * Determines the rule value to show
   *
   * @param usageRatingRule The Usage Rating Rule to get the value for
   */
  public getRuleValue(rule: InterACT.Interfaces.UsageRatingRules.UsageRatingRule) {
    if (rule.type === 'RATING_FLOW_SELECTION') {
      return this.translate.instant(`USAGE_RATING_RULES_DIALOG.USAGE_RATING_RULE.${rule.value}`);
    };

    return rule.value;
  }

  /**
   * Shows the usage rating dialog for creation and editing
   *
   * @param usageRatingRule If editing a usage rating, this will represent the usageRatingRule the user wishes to edit
   */
  private showUsageRatingDialog = (usageRatingRule?: any) => {
    if (usageRatingRule) {
      if (typeof usageRatingRule.validFrom === 'string') {
        usageRatingRule.validFrom = <any>new Date(usageRatingRule.validFrom);
      }

      if (typeof usageRatingRule.value === 'string' && usageRatingRule.type !== 'RATING_FLOW_SELECTION') {
        usageRatingRule.value = <any>parseInt(usageRatingRule.value)
      }
    }

    this.dialogService.showUsageRatingDialog(usageRatingRule).then(response => {
      response.options.usageRatingRule.scheme = {
        abbreviation: (<any>this.userService.selectedScheme).abbreviation,
        link: this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id)
      }

      const saveRule = response.options.usageRatingRule.id ?
        this.usageRatingRulesService.update.bind(this.usageRatingRulesService) :
        this.usageRatingRulesService.create.bind(this.usageRatingRulesService);

      saveRule(response.options.usageRatingRule).then(() => {
        this.userMessagesService.showToast('USAGE_RATING_RULE_SAVED');
        this.$state.go('usage-rating-rules');
        this.getUsageRatingRules(1, 15);
      });
    })
  }

  /**
   * A method that constructs a qParam query string based on the selected filters held on this controller
   */
  private constructQueryString() {
    let query = this.queryBuilder.newParam()
      .fieldIs('scheme.link', this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id)).getParam();

    if (this.ruleFilters.validFrom) {
      query += ' and validFrom >= \'' + new Date(this.ruleFilters.validFrom).toISOString() + '\'';
    }

    query += ' sortBy created desc';

    return query;
  }

  /**
   * A method that opens a new usage rating dialog ready for creation
   */
  private createUsageRatingRule() {
    this.showUsageRatingDialog();
  }

  /**
   * A method that is called during controller instantiation
   */
  private activate() {
    this.PrimaryActionService.setActions([{
      name: 'CREATE_USAGE_RATING_RULE',
      action: this.createUsageRatingRule.bind(this)
    }]);

    this.toolbarService.getToolbar('primary').then((toolbar) => {
      toolbar.setTitle('USAGE_RATING_RULES');
    });

    this.breadcrumbService.setBreadcrumbs([{
      name: 'USAGE_RATING_RULES',
      sref: '#'
    }]);
  }
}